import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';
import { Textarea } from '../../../ui/form/Textarea';
import timeawayApi from '../../../network/timeaway';

interface BulkRejectModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  ids: string[];
  onRejectSuccess?: () => void;
}

export default function BulkRejectModal({
  open,
  setOpen,
  ids,
  onRejectSuccess,
}: BulkRejectModalProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const count = useMemo(() => ids.length, [ids.length]);

  const handleReject = async () => {
    if (!ids.length) return;
    if (!reason.trim()) {
      alert('A reason for rejection is required.');
      return;
    }
    try {
      setIsSubmitting(true);
      await timeawayApi.bulkRejectTimeawayRequests(ids, reason.trim());
      setOpen(false);
      setReason('');
      onRejectSuccess?.();
    } catch (error: any) {
      console.error('Failed to bulk reject requests:', error);
      alert(
        `Failed to reject requests. ${
          error.response?.data?.message || error.message
        }`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center ">
          <DialogPanel
            transition
            className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in "
          >
            <div className="bg-white px-6 pt-2">
              <div className="flex flex-col">
                <div className="mt-4 flex justify-between">
                  <DialogTitle as="h6" className="font-semibold text-gray-900">
                    Reject {count} request{count === 1 ? '' : 's'}
                  </DialogTitle>
                  <button onClick={() => setOpen(false)} aria-label="Close dialog">
                    <XMarkIcon className="w-4" />
                  </button>
                </div>
                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    All selected employees will receive a notification about their
                    request being rejected.
                  </p>
                </div>
                <div className="mt-3 flex flex-col gap-1">
                  <p className="font-semibold">Reason</p>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Specify the reason for refusal, ex: insufficient leave balance."
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-end gap-3 px-6 py-5">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-1"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="rounded-lg bg-[#DC2626] px-4 py-1 text-white"
                disabled={isSubmitting || !ids.length}
              >
                {isSubmitting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

